import React, { useState } from 'react';
import {
  Copy,
  Check,
  ChevronDown,
  ChevronRight,
  Phone,
  MessageSquare,
  Mail,
  Smartphone,
  Users,
  CreditCard,
  Webhook,
  Search,
  Play,
  X,
  Key,
  User,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  ChevronUp,
  AlertTriangle,
  Send,
} from 'lucide-react';
import axiosInstance from '../../api/axios.config';
import axios from 'axios';

// Define scope type locally for documentation purposes
type ApiScope = 'READ' | 'WRITE' | 'ADMIN';

interface ApiEndpoint {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: string;
  description: string;
  requiredScopes: ApiScope[];
  requestBody?: string;
  responseExample?: string;
  queryParams?: Array<{ name: string; type: string; required: boolean; description: string }>;
  pathParams?: Array<{ name: string; type: string; description: string }>;
  /** Conditional rules and gotchas — when a field is required, aliases, error behaviour. */
  notes?: string[];
}

interface ApiSection {
  id: string;
  name: string;
  icon: React.ElementType;
  description: string;
  baseUrl: string;
  endpoints: ApiEndpoint[];
}

const apiSections: ApiSection[] = [
  {
    id: 'voice',
    name: 'Voice / Telephony',
    icon: Phone,
    description: 'Place and manage outbound calls',
    baseUrl: '/api/telephony',
    endpoints: [
      {
        method: 'POST',
        path: '/calls',
        description: 'Place an outbound call',
        requiredScopes: ['WRITE'],
        requestBody: JSON.stringify({
          businessId: 'business-uuid',
          from: '+911234567890',
          to: '+919876543210',
          headers: { 'X-Customer-Id': '456' }
        }, null, 2),
        responseExample: JSON.stringify({
          success: true,
          message: 'call placed',
          data: {
            status: 'ADMITTED',
            callId: 'call_abc123',
            leaseId: 'lease_xyz',
            provider: 'tata1',
            reason: null,
            detail: null
          }
        }, null, 2),
        notes: [
          'businessId and to are both required — either missing returns HTTP 400.',
          'headers is an optional map of custom SIP headers passed through to the provider.',
          'HTTP 429 means the call was throttled by a capacity or rate limit; data.reason and data.detail explain which.',
          'HTTP 422 means origination failed outright (no healthy provider, rejected by carrier). Both 429 and 422 still return a populated data object — read data.status rather than relying on the HTTP code alone.',
          'Provider selection is automatic, by configured priority among healthy providers.'
        ]
      },
      {
        method: 'POST',
        path: '/calls/{callId}/hangup',
        description: 'Terminate an active call',
        requiredScopes: ['WRITE'],
        pathParams: [{ name: 'callId', type: 'string', description: 'The callId returned when the call was placed' }],
        responseExample: JSON.stringify({ success: true, message: 'Success', data: null }, null, 2),
        notes: [
          'Returns HTTP 404 with {"success":false} if the callId is unknown or the call already ended.',
          'This releases the capacity lease held by the call.'
        ]
      }
    ]
  },
  {
    id: 'sms',
    name: 'SMS',
    icon: Mail,
    description: 'Send SMS and manage sender IDs',
    baseUrl: '/api/sms',
    endpoints: [
      {
        method: 'POST',
        path: '/messages/send',
        description: 'Send an SMS to one or more recipients',
        requiredScopes: ['WRITE'],
        requestBody: JSON.stringify({
          toNumbers: ['+919876543210', '+919876543211'],
          senderId: 'MYBRND',
          message: 'Your OTP is 123456',
          type: 'TRANSACTIONAL'
        }, null, 2),
        responseExample: JSON.stringify({
          success: true,
          message: 'Success',
          data: [
            { id: 'uuid', toNumber: '+919876543210', status: 'QUEUED', senderId: 'MYBRND' }
          ]
        }, null, 2),
        notes: [
          'toNumbers is an array, even for a single recipient — there is no singular "to" field.',
          'data is an array with one entry per recipient, so a partial batch failure shows per-number.',
          'senderId must already be registered and approved for the organization (see /sender-ids).',
          'type is TRANSACTIONAL or PROMOTIONAL. Promotional traffic is subject to DND filtering.',
          'To schedule, set schedule: true together with scheduleTime; otherwise it sends immediately.',
          'contactListId can be supplied instead of toNumbers to send to a saved contact list.'
        ]
      },
      {
        method: 'GET',
        path: '/messages',
        description: 'List SMS messages',
        requiredScopes: ['READ'],
        queryParams: [
          { name: 'page', type: 'number', required: false, description: 'Page number, 0-based' },
          { name: 'size', type: 'number', required: false, description: 'Page size' },
          { name: 'status', type: 'string', required: false, description: 'QUEUED, SENT, DELIVERED or FAILED' }
        ],
        responseExample: JSON.stringify({
          success: true,
          message: 'Success',
          data: { content: [{ id: 'uuid', toNumber: '+919876543210', status: 'DELIVERED' }], totalElements: 42 }
        }, null, 2),
        notes: ['Delivery receipts arrive asynchronously, so status can change after the send call returns.']
      },
      {
        method: 'GET',
        path: '/sender-ids',
        description: 'List registered sender IDs',
        requiredScopes: ['READ'],
        responseExample: JSON.stringify({
          success: true,
          message: 'Success',
          data: [{ id: 1, senderId: 'MYBRND', status: 'APPROVED' }]
        }, null, 2),
        notes: [
          'Only APPROVED sender IDs can be used to send.',
          'Sender IDs are typically 6 alphanumeric characters and require carrier approval.'
        ]
      }
    ]
  },
  {
    id: 'whatsapp',
    name: 'WhatsApp',
    icon: MessageSquare,
    description: 'WhatsApp Business API',
    baseUrl: '/api/v1/whatsapp',
    endpoints: [
      {
        method: 'POST',
        path: '/messages/send',
        description: 'Send a message of any type (TEXT, TEMPLATE, IMAGE, DOCUMENT, AUDIO, VIDEO, LOCATION, INTERACTIVE, REACTION)',
        requiredScopes: ['WRITE'],
        requestBody: JSON.stringify({
          to: '+919876543210',
          type: 'TEMPLATE',
          templateName: 'order_confirmation',
          templateLanguage: 'en',
          templateParameters: { '1': 'John', '2': 'ORD123' }
        }, null, 2),
        responseExample: JSON.stringify({
          success: true,
          message: 'Success',
          data: { messageId: 'wamid.HBgMOTE5ODc2NTQzMjEwFQIAERgS...' }
        }, null, 2),
        notes: [
          'to is always required, in E.164 format (e.g. +919876543210).',
          'type is upper-cased by the server; it defaults to TEXT when omitted.',
          'TEXT requires text. TEMPLATE requires templateName (templateLanguage defaults to "en").',
          'IMAGE, DOCUMENT, AUDIO and VIDEO require mediaUrl — a publicly reachable HTTPS URL. caption is optional for IMAGE/DOCUMENT/VIDEO; filename is optional for DOCUMENT.',
          'LOCATION requires both latitude and longitude; locationName and locationAddress are optional.',
          'INTERACTIVE requires bodyText, plus interactiveType "button" (buttons[], max 3) or "list" (buttonText + sections[]). Anything else is rejected.',
          'REACTION requires reactionMessageId and emoji, and returns {"status":"sent"} instead of a messageId. Send an empty emoji to remove a reaction.',
          'phoneNumberId is optional — the organization\'s primary number is used when omitted. contactId and campaignId are optional tracking fields.',
          'templateParameters reserved keys: numeric "1","2",… fill body variables in order; header_image / header_video / header_document / header_text set the header; header_document_filename names a document header; button_url_<i> fills a dynamic URL button; button_payload_<i> sets a quick-reply payload.',
          'Omitting templateParameters entirely is valid for templates with no variables.',
          'Validation failures return HTTP 400 and send failures HTTP 500, both with {"success":false,"message":"..."}.'
        ]
      },
      {
        method: 'POST',
        path: '/messages/send/text',
        description: 'Send a plain text message (convenience endpoint)',
        requiredScopes: ['WRITE'],
        requestBody: JSON.stringify({
          to: '+919876543210',
          text: 'Hello from the API'
        }, null, 2),
        responseExample: JSON.stringify({
          success: true,
          message: 'Success',
          data: { messageId: 'wamid.HBgMOTE5ODc2NTQzMjEwFQIAERgS...' }
        }, null, 2),
        notes: [
          'to and text are both required and must be non-blank.',
          'phoneNumberId and contactId are optional.',
          'Free-form text only reaches users inside the 24-hour customer service window; outside it, use a template.'
        ]
      },
      {
        method: 'POST',
        path: '/messages/send/template',
        description: 'Send a template message (convenience endpoint)',
        requiredScopes: ['WRITE'],
        requestBody: JSON.stringify({
          to: '+919876543210',
          templateName: 'order_confirmation',
          templateLanguage: 'en',
          parameters: { '1': 'John', '2': 'ORD123' }
        }, null, 2),
        responseExample: JSON.stringify({
          success: true,
          message: 'Success',
          data: { messageId: 'wamid.HBgMOTE5ODc2NTQzMjEwFQIAERgS...' }
        }, null, 2),
        notes: [
          'The variables map is named parameters here, but templateParameters on /messages/send. Same reserved keys apply to both.',
          'to and templateName are required; templateLanguage defaults to "en".',
          'The template must already be APPROVED for this organization, and templateLanguage must match the approved language exactly.',
          'Common Meta errors: 131049 (frequency cap — recipient not messaged to protect ecosystem engagement), 131026 (number not on WhatsApp / undeliverable), 132012 (header format mismatch — send a header_* parameter), 131008 (a required button parameter is missing).'
        ]
      },
      {
        method: 'GET',
        path: '/messages',
        description: 'List messages with pagination and filters',
        requiredScopes: ['READ'],
        queryParams: [
          { name: 'page', type: 'number', required: false, description: 'Page number, 0-based (default 0)' },
          { name: 'size', type: 'number', required: false, description: 'Page size (default 20)' },
          { name: 'sortBy', type: 'string', required: false, description: 'Sort field (default sentAt)' },
          { name: 'sortDirection', type: 'string', required: false, description: 'asc or desc (default desc)' },
          { name: 'direction', type: 'string', required: false, description: 'INBOUND or OUTBOUND' },
          { name: 'status', type: 'string', required: false, description: 'QUEUED, SENT, DELIVERED, READ or FAILED' },
          { name: 'fromMillis', type: 'number', required: false, description: 'Start of range, epoch milliseconds' },
          { name: 'toMillis', type: 'number', required: false, description: 'End of range, epoch milliseconds' }
        ],
        responseExample: JSON.stringify({
          success: true,
          message: 'Success',
          data: {
            content: [{ id: 'uuid', contactPhone: '+919876543210', direction: 'OUTBOUND', type: 'TEMPLATE', status: 'DELIVERED', sentAt: 1753180147000 }],
            page: 0, size: 20, totalElements: 2333, totalPages: 117, last: false
          }
        }, null, 2),
        notes: [
          'Pagination is 0-based and the rows live under data.content, not at the top level.',
          'All timestamps are epoch milliseconds.',
          'Delivery outcomes arrive asynchronously by webhook, so a message can be SENT here and only become DELIVERED, READ or FAILED later.'
        ]
      },
      {
        method: 'POST',
        path: '/templates',
        description: 'Create a template and submit it to Meta for approval',
        requiredScopes: ['WRITE'],
        requestBody: JSON.stringify({
          name: 'order_confirmation',
          language: 'en',
          category: 'MARKETING',
          bodyText: 'Hi {{1}}, your order {{2}} is confirmed.',
          footerText: 'Green Giraffe',
          buttonText: '[{"type":"QUICK_REPLY","text":"Track order"},{"type":"URL","text":"Visit","url":"https://example.com"}]'
        }, null, 2),
        responseExample: JSON.stringify({
          success: true,
          message: 'Success',
          data: {
            id: 'uuid', name: 'order_confirmation', metaTemplateId: '1334719442148138',
            language: 'en', category: 'MARKETING', status: 'PENDING',
            bodyText: 'Hi {{1}}, your order {{2}} is confirmed.', parameterCount: 2,
            buttonText: '[{"type":"QUICK_REPLY","text":"Track order"}]', createdAt: 1753180147000
          }
        }, null, 2),
        notes: [
          'Field names differ from the stored model: send name (not templateName) and buttonText (not buttons). Responses use name, buttonText and metaTemplateId.',
          'name, language, category and bodyText are all required. category must be MARKETING, UTILITY or AUTHENTICATION.',
          'The name must be unique within the organization, and at most 255 characters.',
          'Body variables use {{1}}, {{2}}, … and example values are generated automatically for Meta review.',
          'buttonText accepts a JSON array string, or plain comma-separated labels ("Yes,No,Later") which become separate quick replies. A single quick reply containing commas is split automatically. Maximum 10 buttons.',
          'For an IMAGE, VIDEO or DOCUMENT header set headerType and headerMediaUrl — upload the file to /templates/header-media first. Meta needs an uploaded media handle, so a bare URL alone will not work, and the organization must have a Facebook App ID configured.',
          'The template is submitted to Meta synchronously and comes back PENDING; approval happens later. Poll this endpoint or run /templates/sync.',
          'Business errors return HTTP 200 with {"success":false,"message":"..."} — check the success flag rather than the status code.'
        ]
      },
      {
        method: 'PUT',
        path: '/templates/{id}',
        description: 'Update a template stored locally',
        requiredScopes: ['WRITE'],
        pathParams: [{ name: 'id', type: 'string', description: 'Local template ID (not the Meta template ID)' }],
        requestBody: JSON.stringify({
          name: 'order_confirmation',
          language: 'en',
          category: 'UTILITY',
          bodyText: 'Hi {{1}}, your order {{2}} has shipped.',
          footerText: 'Green Giraffe',
          buttonText: '[{"type":"QUICK_REPLY","text":"Track order"}]'
        }, null, 2),
        responseExample: JSON.stringify({
          success: true,
          message: 'Success',
          data: { id: 'uuid', name: 'order_confirmation', category: 'UTILITY', status: 'APPROVED' }
        }, null, 2),
        notes: [
          'This edits the local record only — it does NOT resubmit to Meta, so the approved template that recipients receive is unchanged. To change what Meta has, create a new template or edit it in Business Manager and then call /templates/sync.',
          'Editable: category, bodyText, headerText, footerText, buttonText, headerType, headerMediaUrl. name and language are ignored.',
          'name, language, category and bodyText must still be present to pass validation, even though name and language are ignored.',
          'headerText, footerText and buttonText are overwritten unconditionally — omit one and it is cleared. headerMediaUrl and headerType are only replaced when supplied.'
        ]
      },
      {
        method: 'POST',
        path: '/templates/header-media',
        description: 'Upload a header image, video or document for a template',
        requiredScopes: ['WRITE'],
        responseExample: JSON.stringify({
          success: true,
          message: 'Success',
          data: { url: 'https://gg-cpaas-assets.s3.ap-south-1.amazonaws.com/templates/banner.jpg' }
        }, null, 2),
        notes: [
          'Send as multipart/form-data with the file in a field named "file" — not JSON.',
          'Pass the returned url back as headerMediaUrl, together with headerType, when creating the template.',
          'Maximum upload size is 25 MB.'
        ]
      },
      {
        method: 'GET',
        path: '/templates',
        description: 'List templates with pagination',
        requiredScopes: ['READ'],
        queryParams: [
          { name: 'page', type: 'number', required: false, description: 'Page number, 0-based (default 0)' },
          { name: 'size', type: 'number', required: false, description: 'Page size (default 20)' },
          { name: 'sortBy', type: 'string', required: false, description: 'Sort field (default templateName)' },
          { name: 'sortDirection', type: 'string', required: false, description: 'asc or desc (default asc)' },
          { name: 'status', type: 'string', required: false, description: 'PENDING, APPROVED, REJECTED or DISABLED' }
        ],
        responseExample: JSON.stringify({
          success: true,
          message: 'Success',
          data: {
            content: [{ id: 'uuid', name: 'order_confirmation', metaTemplateId: '1334719442148138', status: 'APPROVED', category: 'MARKETING', language: 'en', parameterCount: 2 }],
            page: 0, size: 20, totalElements: 34, totalPages: 2, last: false
          }
        }, null, 2),
        notes: [
          'Pagination is 0-based and rows live under data.content.',
          'Only APPROVED templates can be sent. Use /templates/approved for a flat, unpaginated list of those.'
        ]
      },
      {
        method: 'POST',
        path: '/templates/sync',
        description: 'Pull the latest template statuses and content from Meta',
        requiredScopes: ['WRITE'],
        responseExample: JSON.stringify({
          success: true,
          message: 'Success',
          data: 12
        }, null, 2),
        notes: [
          'data is the number of templates updated.',
          'Use this after submitting a template to pick up an APPROVED or REJECTED decision.',
          'Refreshes status, header type, buttons and the stored component structure. Your locally set headerMediaUrl is preserved.'
        ]
      },
      {
        method: 'POST',
        path: '/campaigns',
        description: 'Create a bulk template campaign',
        requiredScopes: ['WRITE'],
        requestBody: JSON.stringify({
          name: 'July Promo',
          templateId: 'uuid-of-local-template',
          targetAudience: '{"type":"ALL"}',
          sendRate: 30,
          suppressRecentDays: 7,
          recipients: [{ phone: '+919876543210', variables: { '1': 'John' } }]
        }, null, 2),
        responseExample: JSON.stringify({
          success: true,
          message: 'Success',
          data: { id: 'uuid', name: 'July Promo', status: 'DRAFT', totalRecipients: 1, messagesSent: 0 }
        }, null, 2),
        notes: [
          'name, templateId and targetAudience are required. templateId is the local template ID, not the Meta ID.',
          'targetAudience is a JSON string, e.g. {"type":"ALL"}, {"type":"TAG","value":"leads"}, {"type":"CONTACTS","ids":[...]} or {"type":"NUMBERS","numbers":[...]}.',
          'When recipients[] is supplied it takes precedence over targetAudience and allows per-recipient variables (numeric keys "1","2",…).',
          'sendRate is messages per minute (default 10). The sender slows itself automatically if Meta starts returning 131049 frequency caps, and auto-pauses if they exceed 60% of recent sends.',
          'suppressRecentDays skips anyone already messaged within that many days; leave null or 0 to disable.',
          'scheduledAt is epoch milliseconds — omit it to send as soon as you call /start.',
          'Campaigns are created in DRAFT. Call POST /campaigns/{id}/start to begin.'
        ]
      },
      {
        method: 'POST',
        path: '/campaigns/{id}/start',
        description: 'Start a campaign, or resume a paused one',
        requiredScopes: ['WRITE'],
        pathParams: [{ name: 'id', type: 'string', description: 'Campaign ID' }],
        responseExample: JSON.stringify({
          success: true,
          message: 'Success',
          data: { id: 'uuid', name: 'July Promo', status: 'RUNNING', totalRecipients: 2333, messagesSent: 201 }
        }, null, 2),
        notes: [
          'Allowed only from DRAFT, SCHEDULED or PAUSED — any other status is rejected.',
          'Resuming a PAUSED campaign continues with the recipients still PENDING; those already sent are not messaged again.',
          'Recipients that failed are not retried here — use POST /campaigns/{id}/resend-failed for those.',
          'Sending runs in the background, so this returns immediately. Poll GET /campaigns/{id} for progress.',
          'Pause with POST /campaigns/{id}/pause; the send loop stops within one send interval.'
        ]
      }
    ]
  },
  {
    id: 'rcs',
    name: 'RCS',
    icon: Smartphone,
    description: 'Rich Communication Services',
    baseUrl: '/api/rcs',
    endpoints: [
      {
        method: 'POST',
        path: '/messages/send',
        description: 'Send an RCS message',
        requiredScopes: ['WRITE'],
        requestBody: JSON.stringify({
          agentId: 1,
          recipientPhone: '+919876543210',
          messageContent: 'Hello from RCS',
          messageType: 'TEXT',
          fallbackToSms: true,
          smsFallbackContent: 'Hello (sent by SMS)'
        }, null, 2),
        responseExample: JSON.stringify({
          id: 1,
          agentId: 1,
          recipientPhone: '+919876543210',
          status: 'SENT',
          messageType: 'TEXT'
        }, null, 2),
        notes: [
          'Requires the X-Organization-Id header in addition to your auth credentials — the org is not taken from the token on RCS endpoints.',
          'agentId (numeric) and recipientPhone are required. agentId refers to an RCS agent created via /agents.',
          'Use recipientPhones (array) instead of recipientPhone to send to several numbers at once.',
          'For a rich card, set richCardId and leave messageContent empty; for plain text set messageContent.',
          'fallbackToSms delivers smsFallbackContent by SMS when the handset or network does not support RCS.',
          'Returns HTTP 201 Created, and the body is the message object directly — NOT wrapped in a success/data envelope.'
        ]
      },
      {
        method: 'GET',
        path: '/agents',
        description: 'List RCS agents',
        requiredScopes: ['READ'],
        queryParams: [
          { name: 'page', type: 'number', required: false, description: 'Page number, 0-based' },
          { name: 'size', type: 'number', required: false, description: 'Page size' }
        ],
        responseExample: JSON.stringify({
          content: [{ id: 1, name: 'My Brand', status: 'VERIFIED' }],
          totalElements: 1, totalPages: 1, number: 0, size: 20
        }, null, 2),
        notes: [
          'Requires the X-Organization-Id header.',
          'Returns a raw Spring Page (fields number/size/totalElements) — not the success/data envelope used elsewhere.',
          'Only VERIFIED agents can send. Submit a new agent with POST /agents/{id}/submit-verification.'
        ]
      },
      {
        method: 'POST',
        path: '/campaigns/{id}/resume',
        description: 'Resume a paused RCS campaign',
        requiredScopes: ['WRITE'],
        pathParams: [{ name: 'id', type: 'string', description: 'Campaign ID' }],
        responseExample: JSON.stringify({ id: 1, name: 'Promo', status: 'RUNNING' }, null, 2),
        notes: [
          'Requires the X-Organization-Id header.',
          'RCS campaigns have an explicit /resume endpoint, unlike WhatsApp campaigns where /start doubles as resume.',
          'Related: POST /campaigns/{id}/start and POST /campaigns/{id}/pause.'
        ]
      }
    ]
  },
  {
    id: 'contacts',
    name: 'Contacts',
    icon: Users,
    description: 'Manage contacts',
    baseUrl: '/api/v1/contacts',
    endpoints: [
      {
        method: 'POST',
        path: '',
        description: 'Create a contact',
        requiredScopes: ['WRITE'],
        requestBody: JSON.stringify({
          firstName: 'John',
          lastName: 'Doe',
          company: 'Acme Ltd',
          source: 'API',
          tags: ['lead'],
          phoneNumbers: [{ number: '+919876543210', isPrimary: true }],
          emails: [{ email: 'john@example.com', isPrimary: true }]
        }, null, 2),
        responseExample: JSON.stringify({
          success: true,
          message: 'Success',
          data: { id: 'uuid', firstName: 'John', lastName: 'Doe', phoneNumbers: [{ id: 'uuid', number: '+919876543210', isPrimary: true }] }
        }, null, 2),
        notes: [
          'There is no top-level phoneNumber field — numbers go in the phoneNumbers array as objects.',
          'Inside a phone entry the field is number, though phoneNumber is also accepted as an alias.',
          'Mark one entry isPrimary: true — the primary number is what campaigns and messaging use.',
          'Either firstName/lastName or the single name field may be used.',
          'This controller answers on both /api/contacts and /api/v1/contacts.'
        ]
      },
      {
        method: 'GET',
        path: '',
        description: 'List contacts',
        requiredScopes: ['READ'],
        queryParams: [
          { name: 'page', type: 'number', required: false, description: 'Page number, 0-based' },
          { name: 'size', type: 'number', required: false, description: 'Page size' },
          { name: 'search', type: 'string', required: false, description: 'Match against name or phone number' },
          { name: 'unreadOnly', type: 'boolean', required: false, description: 'Only contacts with unread inbound messages' },
          { name: 'recentDays', type: 'number', required: false, description: 'Only contacts active in the last N days' },
          { name: 'assignedTo', type: 'string', required: false, description: 'Filter by assigned agent user ID' }
        ],
        responseExample: JSON.stringify({
          success: true,
          message: 'Success',
          data: { content: [{ id: 'uuid', name: 'John Doe', lastContactedAt: 1753180147000 }], page: 0, size: 20, totalElements: 512, totalPages: 26, last: false }
        }, null, 2),
        notes: [
          'Pagination is 0-based and rows live under data.content.',
          'search, unreadOnly and recentDays are applied in the database, so they cover all contacts rather than the current page.'
        ]
      },
      {
        method: 'POST',
        path: '/import',
        description: 'Bulk import contacts from a file',
        requiredScopes: ['WRITE'],
        responseExample: JSON.stringify({
          success: true,
          message: 'Success',
          data: { totalRows: 500, imported: 486, skipped: 14, errors: [] }
        }, null, 2),
        notes: [
          'Send as multipart/form-data, not JSON.',
          'Download the expected column layout from GET /v1/contacts/import/template first.',
          'Existing contacts are matched on phone number and skipped rather than duplicated.'
        ]
      }
    ]
  },
  {
    id: 'billing',
    name: 'Billing',
    icon: CreditCard,
    description: 'Credit accounts, rates and entitlement',
    baseUrl: '/api/billing',
    endpoints: [
      {
        method: 'GET',
        path: '/entitlement',
        description: 'Check whether the organization may spend, and at what rates',
        requiredScopes: ['READ'],
        responseExample: JSON.stringify({
          organizationId: 'uuid',
          account: {
            active: true,
            billingType: 'PREPAID',
            currency: 'INR',
            creditBalance: 1500.50,
            creditLimit: 0,
            lowBalance: false,
            spendable: true,
            subscriptionStatus: 'ACTIVE',
            subscriptionPeriodEnd: '2026-08-22T00:00:00'
          },
          rates: [{ category: 'whatsapp_template', rate: 0.85, currency: 'INR' }]
        }, null, 2),
        notes: [
          'spendable is the single flag to gate on. It is true when the account is active AND either an ACTIVE/TRIALING subscription is in its period, or the balance allows it.',
          'For PREPAID the balance rule is balance > 0; for POSTPAID it is balance > -creditLimit.',
          'The organization comes from your token, or from the X-Organization-Id header.',
          'Returns HTTP 404 when the organization has no credit account.',
          'Rates fall back to the platform default when no organization-specific rate is set.'
        ]
      },
      {
        method: 'GET',
        path: '/accounts/organization/{organizationId}/balance',
        description: 'Get the current credit balance',
        requiredScopes: ['READ'],
        pathParams: [{ name: 'organizationId', type: 'string', description: 'Organization ID' }],
        responseExample: '1500.50',
        notes: [
          'Returns a bare number, not an object or envelope.',
          'For the fuller picture including spendability, use /entitlement instead.'
        ]
      },
      {
        method: 'POST',
        path: '/credits/add',
        description: 'Add credits to an organization',
        requiredScopes: ['ADMIN'],
        requestBody: JSON.stringify({
          organizationId: 'uuid',
          amount: 5000,
          description: 'Top-up via bank transfer',
          referenceId: 'INV-2026-001'
        }, null, 2),
        responseExample: JSON.stringify({
          id: 1, organizationId: 'uuid', amount: 5000, type: 'CREDIT', balanceAfter: 6500.50
        }, null, 2),
        notes: [
          'organizationId and amount are required.',
          'Admin-only — this moves real money-equivalent balance.',
          'To record a gateway payment with an audit trail, use POST /payments instead; it can add credits and store the raw gateway response.',
          'Responses on billing endpoints are returned directly, without the success/data envelope.'
        ]
      },
      {
        method: 'GET',
        path: '/rates',
        description: 'List service rates',
        requiredScopes: ['READ'],
        queryParams: [
          { name: 'page', type: 'number', required: false, description: 'Page number, 0-based' },
          { name: 'size', type: 'number', required: false, description: 'Page size' }
        ],
        responseExample: JSON.stringify({
          content: [{ id: 1, serviceType: 'WHATSAPP_TEMPLATE', rate: 0.85, currency: 'INR' }],
          totalElements: 8, number: 0, size: 20
        }, null, 2),
        notes: [
          'Returns a raw Spring Page (number/size/totalElements), not the success/data envelope.',
          'GET /rates/default lists the platform-wide fallback rates used when an organization has no override.'
        ]
      }
    ]
  },
  {
    id: 'webhooks',
    name: 'Webhooks',
    icon: Webhook,
    description: 'Register endpoints to receive events',
    baseUrl: '/api/developer/webhooks',
    endpoints: [
      {
        method: 'POST',
        path: '',
        description: 'Create a webhook subscription',
        requiredScopes: ['WRITE'],
        requestBody: JSON.stringify({
          name: 'Call Events',
          url: 'https://your-server.com/webhook',
          webhookType: 'TELEPHONY',
          eventTypes: ['CALL_COMPLETED', 'CALL_FAILED'],
          httpMethod: 'POST',
          customHeaders: { 'X-Secret': 'your-shared-secret' },
          maxRetries: 3
        }, null, 2),
        responseExample: JSON.stringify({
          success: true,
          message: 'Success',
          data: { id: 'uuid', name: 'Call Events', url: 'https://your-server.com/webhook', enabled: true }
        }, null, 2),
        notes: [
          'name, url and webhookType are all required — webhookType is easy to miss and the request fails without it.',
          'The field is eventTypes (array), not events.',
          'Your endpoint must answer 2xx quickly; non-2xx replies are retried up to maxRetries with backoff.',
          'Use customHeaders to carry a shared secret so you can verify that deliveries came from us.',
          'Send a sample payload with POST /webhooks/{webhookId}/test before going live.'
        ]
      },
      {
        method: 'GET',
        path: '/{webhookId}/logs',
        description: 'Inspect recent delivery attempts',
        requiredScopes: ['READ'],
        pathParams: [{ name: 'webhookId', type: 'string', description: 'Webhook ID' }],
        responseExample: JSON.stringify({
          success: true,
          message: 'Success',
          data: { content: [{ id: 'uuid', responseStatus: 200, attempt: 1, deliveredAt: 1753180147000 }] }
        }, null, 2),
        notes: [
          'Shows the response status and body we received, which is the quickest way to debug a failing endpoint.',
          'Replay a single failed delivery with POST /webhooks/logs/{logId}/retry.',
          'Temporarily stop deliveries with POST /webhooks/{webhookId}/disable rather than deleting the subscription.'
        ]
      }
    ]
  }
];

// Types for tester
type AuthMode = 'session' | 'apiKey' | 'bearer';

interface TestResponse {
  status: number;
  statusText: string;
  data: any;
  headers: Record<string, string>;
  duration: number;
}

interface SelectedEndpoint {
  section: ApiSection;
  endpoint: ApiEndpoint;
  index: number;
}

const ApiDocumentation: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({ voice: true });
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<'curl' | 'javascript' | 'python'>('curl');

  // Selected endpoint for tester
  const [selectedEndpoint, setSelectedEndpoint] = useState<SelectedEndpoint | null>(null);

  // Tester state
  const [pathParamValues, setPathParamValues] = useState<Record<string, string>>({});
  const [queryParamValues, setQueryParamValues] = useState<Record<string, string>>({});
  const [requestBody, setRequestBody] = useState('');
  const [response, setResponse] = useState<TestResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showHeaders, setShowHeaders] = useState(false);
  const [copiedResponse, setCopiedResponse] = useState(false);

  // Auth state
  const [authMode, setAuthMode] = useState<AuthMode>('session');
  const [apiKey, setApiKey] = useState('');
  const [bearerToken, setBearerToken] = useState('');

  const baseApiUrl = 'https://api.teeoff.com';
  const apiBaseUrl = '';

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const selectEndpoint = (section: ApiSection, endpoint: ApiEndpoint, index: number) => {
    setSelectedEndpoint({ section, endpoint, index });
    setRequestBody(endpoint.requestBody || '');
    setPathParamValues({});
    setQueryParamValues({});
    setResponse(null);
    setError(null);
  };

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(key);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const getMethodColor = (method: string) => {
    const colors: Record<string, string> = {
      GET: 'bg-green-500 text-white',
      POST: 'bg-blue-500 text-white',
      PUT: 'bg-yellow-500 text-white',
      PATCH: 'bg-orange-500 text-white',
      DELETE: 'bg-red-500 text-white'
    };
    return colors[method] || 'bg-gray-500 text-white';
  };

  const getMethodBorderColor = (method: string) => {
    const colors: Record<string, string> = {
      GET: 'border-l-green-500',
      POST: 'border-l-blue-500',
      PUT: 'border-l-yellow-500',
      PATCH: 'border-l-orange-500',
      DELETE: 'border-l-red-500'
    };
    return colors[method] || 'border-l-gray-500';
  };

  const generateCodeSample = (section: ApiSection, endpoint: ApiEndpoint): string => {
    const fullUrl = `${baseApiUrl}${section.baseUrl}${endpoint.path}`;

    if (selectedLanguage === 'curl') {
      let curl = `curl -X ${endpoint.method} "${fullUrl}"`;
      curl += `\n  -H "Authorization: Bearer YOUR_API_KEY"`;
      curl += `\n  -H "Content-Type: application/json"`;
      if (endpoint.requestBody) {
        curl += `\n  -d '${endpoint.requestBody.replace(/\n/g, '\n  ')}'`;
      }
      return curl;
    }

    if (selectedLanguage === 'javascript') {
      let js = `const response = await fetch("${fullUrl}", {
  method: "${endpoint.method}",
  headers: {
    "Authorization": "Bearer YOUR_API_KEY",
    "Content-Type": "application/json"
  }`;
      if (endpoint.requestBody) {
        js += `,
  body: JSON.stringify(${endpoint.requestBody})`;
      }
      js += `
});
const data = await response.json();`;
      return js;
    }

    if (selectedLanguage === 'python') {
      let py = `import requests

response = requests.${endpoint.method.toLowerCase()}(
    "${fullUrl}",
    headers={"Authorization": "Bearer YOUR_API_KEY"}`;
      if (endpoint.requestBody) {
        py += `,
    json=${endpoint.requestBody}`;
      }
      py += `
)
print(response.json())`;
      return py;
    }

    return '';
  };

  // Build URL for tester
  const buildUrl = (): string => {
    if (!selectedEndpoint) return '';
    let url = selectedEndpoint.endpoint.path;

    // Replace path params
    if (selectedEndpoint.endpoint.pathParams) {
      selectedEndpoint.endpoint.pathParams.forEach(param => {
        const value = pathParamValues[param.name] || `{${param.name}}`;
        url = url.replace(`{${param.name}}`, encodeURIComponent(value));
      });
    }

    // Add query params
    const queryEntries = Object.entries(queryParamValues).filter(([_, v]) => v);
    if (queryEntries.length > 0) {
      const queryString = queryEntries
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join('&');
      url += `?${queryString}`;
    }

    return url;
  };

  // Execute API request
  const executeRequest = async () => {
    if (!selectedEndpoint) return;

    const { section, endpoint } = selectedEndpoint;

    // Validate path params
    if (endpoint.pathParams) {
      const missingParams = endpoint.pathParams.filter(p => !pathParamValues[p.name]);
      if (missingParams.length > 0) {
        setError(`Missing required path parameters: ${missingParams.map(p => p.name).join(', ')}`);
        return;
      }
    }

    // Validate required query params
    if (endpoint.queryParams) {
      const missingRequired = endpoint.queryParams.filter(p => p.required && !queryParamValues[p.name]);
      if (missingRequired.length > 0) {
        setError(`Missing required query parameters: ${missingRequired.map(p => p.name).join(', ')}`);
        return;
      }
    }

    setIsLoading(true);
    setError(null);
    setResponse(null);

    const startTime = performance.now();
    const url = buildUrl();

    try {
      let parsedBody: any = undefined;
      if (requestBody && ['POST', 'PUT', 'PATCH'].includes(endpoint.method)) {
        try {
          parsedBody = JSON.parse(requestBody);
        } catch (e) {
          setError('Invalid JSON in request body');
          setIsLoading(false);
          return;
        }
      }

      const config: any = {
        method: endpoint.method.toLowerCase(),
        url: `${apiBaseUrl}${section.baseUrl}${url}`,
        headers: { 'Content-Type': 'application/json' },
      };

      if (parsedBody) {
        config.data = parsedBody;
      }

      let res;

      if (authMode === 'session') {
        res = await axiosInstance(config);
      } else {
        if (authMode === 'apiKey') {
          if (!apiKey.trim()) {
            setError('Please enter an API key');
            setIsLoading(false);
            return;
          }
          config.headers['X-API-Key'] = apiKey.trim();
        } else if (authMode === 'bearer') {
          if (!bearerToken.trim()) {
            setError('Please enter a bearer token');
            setIsLoading(false);
            return;
          }
          config.headers['Authorization'] = `Bearer ${bearerToken.trim()}`;
        }

        const base = axiosInstance.defaults.baseURL || '';
        config.url = `${base}${config.url}`;
        res = await axios(config);
      }

      const duration = Math.round(performance.now() - startTime);

      setResponse({
        status: res.status,
        statusText: res.statusText,
        data: res.data,
        headers: res.headers as Record<string, string>,
        duration,
      });
    } catch (err: any) {
      const duration = Math.round(performance.now() - startTime);

      if (err.response) {
        setResponse({
          status: err.response.status,
          statusText: err.response.statusText,
          data: err.response.data,
          headers: err.response.headers as Record<string, string>,
          duration,
        });
      } else {
        setError(err.message || 'Request failed');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const copyResponse = () => {
    if (response) {
      navigator.clipboard.writeText(JSON.stringify(response.data, null, 2));
      setCopiedResponse(true);
      setTimeout(() => setCopiedResponse(false), 2000);
    }
  };

  const getStatusColor = (status: number) => {
    if (status >= 200 && status < 300) return 'text-green-600 bg-green-100';
    if (status >= 400 && status < 500) return 'text-yellow-600 bg-yellow-100';
    if (status >= 500) return 'text-red-600 bg-red-100';
    return 'text-gray-600 bg-gray-100';
  };

  const filteredSections = apiSections.filter(section => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      section.name.toLowerCase().includes(query) ||
      section.endpoints.some(ep =>
        ep.path.toLowerCase().includes(query) ||
        ep.description.toLowerCase().includes(query)
      )
    );
  });

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">API Documentation</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Interactive API explorer - Select an endpoint to test</p>
          </div>
          <div className="flex items-center gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 border rounded-lg text-sm w-64 focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            {/* Language Selector */}
            <div className="flex border dark:border-gray-700 rounded-lg overflow-hidden">
              {(['curl', 'javascript', 'python'] as const).map((lang) => (
                <button
                  key={lang}
                  onClick={() => setSelectedLanguage(lang)}
                  className={`px-3 py-1.5 text-sm ${
                    selectedLanguage === lang
                      ? 'bg-blue-600 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                >
                  {lang === 'curl' ? 'cURL' : lang.charAt(0).toUpperCase() + lang.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Side by Side */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Endpoints */}
        <div className="w-1/2 border-r dark:border-gray-700 overflow-y-auto bg-gray-50 dark:bg-gray-900">
          <div className="p-4 space-y-2">
            {filteredSections.map((section) => {
              const SectionIcon = section.icon;
              const isExpanded = expandedSections[section.id];

              return (
                <div key={section.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
                  {/* Section Header */}
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="w-full flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <div className="flex items-center gap-2">
                      <SectionIcon className="w-4 h-4 text-blue-600" />
                      <span className="font-medium text-gray-900 dark:text-white">{section.name}</span>
                      <span className="text-xs text-gray-400 dark:text-gray-500">({section.endpoints.length})</span>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                  </button>

                  {/* Endpoints */}
                  {isExpanded && (
                    <div className="border-t dark:border-gray-700">
                      {section.endpoints.map((endpoint, idx) => {
                        const isSelected = selectedEndpoint?.section.id === section.id && selectedEndpoint?.index === idx;

                        return (
                          <button
                            key={idx}
                            onClick={() => selectEndpoint(section, endpoint, idx)}
                            className={`w-full flex items-center gap-3 p-3 text-left border-l-4 transition-colors ${
                              isSelected
                                ? `bg-blue-50 dark:bg-blue-900/30 ${getMethodBorderColor(endpoint.method)}`
                                : `border-l-transparent hover:bg-gray-50 dark:hover:bg-gray-700`
                            }`}
                          >
                            <span className={`px-2 py-0.5 rounded text-xs font-bold ${getMethodColor(endpoint.method)}`}>
                              {endpoint.method}
                            </span>
                            <span className="font-mono text-sm text-gray-700 dark:text-gray-300 truncate flex-1">
                              {endpoint.path}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Panel - Tester */}
        <div className="w-1/2 flex flex-col overflow-hidden bg-white dark:bg-gray-800">
          {selectedEndpoint ? (
            <>
              {/* Endpoint Header */}
              <div className="border-b dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-700">
                <div className="flex items-center gap-3 mb-2">
                  <span className={`px-3 py-1 rounded text-sm font-bold ${getMethodColor(selectedEndpoint.endpoint.method)}`}>
                    {selectedEndpoint.endpoint.method}
                  </span>
                  <code className="text-sm font-mono text-gray-700 dark:text-gray-300">
                    {selectedEndpoint.section.baseUrl}{selectedEndpoint.endpoint.path}
                  </code>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{selectedEndpoint.endpoint.description}</p>
                <div className="flex gap-2 mt-2">
                  {selectedEndpoint.endpoint.requiredScopes.map((scope) => (
                    <span
                      key={scope}
                      className={`px-2 py-0.5 rounded text-xs ${
                        scope === 'READ' ? 'bg-green-100 text-green-700'
                        : scope === 'WRITE' ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-purple-100 text-purple-700'
                      }`}
                    >
                      {scope}
                    </span>
                  ))}
                </div>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Authentication */}
                <div className="border dark:border-gray-700 rounded-lg">
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 border-b dark:border-gray-700">
                    <div className="flex items-center gap-2">
                      <Key className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      <span className="font-medium text-sm dark:text-white">Authentication</span>
                    </div>
                    <div className="flex gap-1">
                      {(['session', 'apiKey', 'bearer'] as const).map((mode) => (
                        <button
                          key={mode}
                          onClick={() => setAuthMode(mode)}
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            authMode === mode
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
                          }`}
                        >
                          {mode === 'session' ? 'Session' : mode === 'apiKey' ? 'API Key' : 'Bearer'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="p-3">
                    {authMode === 'session' && (
                      <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
                        <User className="w-4 h-4" />
                        Using browser session authentication
                      </div>
                    )}
                    {authMode === 'apiKey' && (
                      <input
                        type="password"
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        placeholder="Enter API key (X-API-Key header)"
                        className="w-full px-3 py-2 border dark:border-gray-600 rounded text-sm font-mono dark:bg-gray-700 dark:text-white"
                      />
                    )}
                    {authMode === 'bearer' && (
                      <input
                        type="password"
                        value={bearerToken}
                        onChange={(e) => setBearerToken(e.target.value)}
                        placeholder="Enter Bearer token"
                        className="w-full px-3 py-2 border dark:border-gray-600 rounded text-sm font-mono dark:bg-gray-700 dark:text-white"
                      />
                    )}
                  </div>
                </div>

                {/* Path Parameters */}
                {selectedEndpoint.endpoint.pathParams && selectedEndpoint.endpoint.pathParams.length > 0 && (
                  <div className="border rounded-lg">
                    <div className="p-3 bg-gray-50 border-b">
                      <span className="font-medium text-sm">Path Parameters</span>
                    </div>
                    <div className="p-3 space-y-2">
                      {selectedEndpoint.endpoint.pathParams.map(param => (
                        <div key={param.name} className="flex items-center gap-2">
                          <label className="w-24 text-sm font-mono text-gray-600">
                            {param.name}<span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={pathParamValues[param.name] || ''}
                            onChange={e => setPathParamValues(prev => ({ ...prev, [param.name]: e.target.value }))}
                            placeholder={param.description}
                            className="flex-1 px-3 py-1.5 border rounded text-sm"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Query Parameters */}
                {selectedEndpoint.endpoint.queryParams && selectedEndpoint.endpoint.queryParams.length > 0 && (
                  <div className="border rounded-lg">
                    <div className="p-3 bg-gray-50 border-b">
                      <span className="font-medium text-sm">Query Parameters</span>
                    </div>
                    <div className="p-3 space-y-2">
                      {selectedEndpoint.endpoint.queryParams.map(param => (
                        <div key={param.name} className="flex items-center gap-2">
                          <label className="w-24 text-sm font-mono text-gray-600">
                            {param.name}{param.required && <span className="text-red-500">*</span>}
                          </label>
                          <input
                            type="text"
                            value={queryParamValues[param.name] || ''}
                            onChange={e => setQueryParamValues(prev => ({ ...prev, [param.name]: e.target.value }))}
                            placeholder={param.description}
                            className="flex-1 px-3 py-1.5 border rounded text-sm"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Conditions & Notes */}
                {selectedEndpoint.endpoint.notes && selectedEndpoint.endpoint.notes.length > 0 && (
                  <div className="border border-amber-200 dark:border-amber-800 rounded-lg">
                    <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800">
                      <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                      <span className="font-medium text-sm text-amber-800 dark:text-amber-300">Conditions &amp; Notes</span>
                    </div>
                    <ul className="p-3 space-y-1.5 list-disc list-inside">
                      {selectedEndpoint.endpoint.notes.map((note, i) => (
                        <li key={i} className="text-sm text-gray-700 dark:text-gray-300">{note}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Request Body */}
                {['POST', 'PUT', 'PATCH'].includes(selectedEndpoint.endpoint.method) && (
                  <div className="border rounded-lg">
                    <div className="p-3 bg-gray-50 border-b">
                      <span className="font-medium text-sm">Request Body</span>
                    </div>
                    <div className="p-3">
                      <textarea
                        value={requestBody}
                        onChange={e => setRequestBody(e.target.value)}
                        rows={6}
                        className="w-full px-3 py-2 border rounded font-mono text-sm"
                        placeholder='{"key": "value"}'
                      />
                    </div>
                  </div>
                )}

                {/* Send Button */}
                <button
                  onClick={executeRequest}
                  disabled={isLoading}
                  className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium ${
                    isLoading
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Send Request
                    </>
                  )}
                </button>

                {/* Error */}
                {error && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-red-700">Error</p>
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  </div>
                )}

                {/* Response */}
                {response && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between p-3 bg-gray-50 border-b">
                      <div className="flex items-center gap-3">
                        {response.status >= 200 && response.status < 300 ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500" />
                        )}
                        <span className={`px-2 py-1 rounded text-sm font-medium ${getStatusColor(response.status)}`}>
                          {response.status} {response.statusText}
                        </span>
                        <span className="flex items-center gap-1 text-sm text-gray-500">
                          <Clock className="w-4 h-4" />
                          {response.duration}ms
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setShowHeaders(!showHeaders)}
                          className="text-sm text-gray-600 hover:text-gray-800"
                        >
                          Headers {showHeaders ? <ChevronUp className="w-3 h-3 inline" /> : <ChevronDown className="w-3 h-3 inline" />}
                        </button>
                        <button
                          onClick={copyResponse}
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          {copiedResponse ? <Check className="w-4 h-4 inline" /> : <Copy className="w-4 h-4 inline" />}
                        </button>
                      </div>
                    </div>

                    {showHeaders && (
                      <div className="p-3 bg-gray-100 border-b text-xs font-mono space-y-1">
                        {Object.entries(response.headers).slice(0, 10).map(([key, value]) => (
                          <div key={key}>
                            <span className="text-gray-600">{key}:</span> <span className="text-gray-800">{value}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="max-h-64 overflow-auto">
                      <pre className="p-3 text-sm font-mono bg-gray-900 text-green-400">
                        {JSON.stringify(response.data, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Code Sample */}
                <div className="border rounded-lg">
                  <div className="flex items-center justify-between p-3 bg-gray-50 border-b">
                    <span className="font-medium text-sm">Code Sample</span>
                    <button
                      onClick={() => copyToClipboard(generateCodeSample(selectedEndpoint.section, selectedEndpoint.endpoint), 'code')}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      {copiedCode === 'code' ? <Check className="w-4 h-4 inline" /> : <Copy className="w-4 h-4 inline" />} Copy
                    </button>
                  </div>
                  <pre className="p-3 text-sm font-mono bg-gray-900 text-gray-100 overflow-x-auto">
                    {generateCodeSample(selectedEndpoint.section, selectedEndpoint.endpoint)}
                  </pre>
                </div>

                {/* Response Example */}
                {selectedEndpoint.endpoint.responseExample && (
                  <div className="border rounded-lg">
                    <div className="p-3 bg-gray-50 border-b">
                      <span className="font-medium text-sm">Example Response</span>
                    </div>
                    <pre className="p-3 text-sm font-mono bg-gray-100 text-gray-700 overflow-x-auto">
                      {selectedEndpoint.endpoint.responseExample}
                    </pre>
                  </div>
                )}
              </div>
            </>
          ) : (
            /* No endpoint selected */
            <div className="flex-1 flex items-center justify-center text-gray-400">
              <div className="text-center">
                <Play className="w-16 h-16 mx-auto mb-4 opacity-20" />
                <p className="text-lg font-medium">Select an endpoint to test</p>
                <p className="text-sm">Click on any endpoint from the left panel</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApiDocumentation;
